// shareReceiver: bridge entre Android Share Intent / iOS Share Extension y
// la app React.
//
// Flujo Android (send-intent plugin):
//   1. Usuario pulsa "Compartir" en otra app → OS lanza nuestra
//      SendIntentActivity (declarada en AndroidManifest.xml).
//   2. SendIntentActivity extiende BridgeActivity → arranca un WebView con
//      nuestra app React.
//   3. React monta, ShareBootstrapper llama initShareReceiver(navigate).
//   4. initShareReceiver llama SendIntent.checkSendIntentReceived() que
//      devuelve { url, type, title, additionalItems? } con URLs file://
//      apuntando a /data/data/<pkg>/files/<nombre> (el plugin las copia ahí).
//   5. Convertimos cada file:// → File[] usando 3 estrategias en cascada:
//        a) fetch(Capacitor.convertFileSrc(url))   ← oficial Capacitor
//        b) Filesystem.readFile({ path, directory: Directory.Data })
//        c) Filesystem.readFile({ path: absUrl })   ← último recurso
//   6. Stash global + navigate a /send para que Send.jsx los recoja.
//
// Diagnóstico: cada paso escribe en consola con prefijo [shareReceiver] y
// en window.__kapeiShareDebug[]. Send.jsx muestra el resumen en pantalla.
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { App as CapacitorApp } from "@capacitor/app";
import { SendIntent } from "send-intent";

const isNative = () => Capacitor.isNativePlatform();

const DEBUG = [];
function dbg(...args) {
  const line = args
    .map((a) => (typeof a === "string" ? a : (() => {
      try { return JSON.stringify(a); } catch (_) { return String(a); }
    })()))
    .join(" ");
  // eslint-disable-next-line no-console
  console.log("[shareReceiver]", ...args);
  DEBUG.push(`${new Date().toISOString().slice(11, 23)} ${line}`);
  if (DEBUG.length > 300) DEBUG.shift();
  if (typeof window !== "undefined") window.__kapeiShareDebug = DEBUG;
}

function base64ToBytes(b64) {
  const binStr = atob(b64);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
  return bytes;
}

// Intenta leer el fichero por 3 estrategias en cascada. Devuelve el blob/bytes
// y el nombre de la estrategia que funcionó. Si las 3 fallan, lanza la última
// excepción (no se traga el error).
async function readFileBytes(absUrl, dbgPrefix) {
  let lastErr = null;

  // Estrategia A: fetch(convertFileSrc) — oficial Capacitor
  try {
    const fetchUrl = Capacitor.convertFileSrc(absUrl);
    dbg(`${dbgPrefix} [A] fetch(convertFileSrc) →`, fetchUrl);
    const resp = await fetch(fetchUrl);
    dbg(`${dbgPrefix} [A] fetch status=${resp.status} ok=${resp.ok}`);
    if (resp.ok) {
      const blob = await resp.blob();
      dbg(`${dbgPrefix} [A] OK size=${blob.size} type=${blob.type}`);
      return { blob, strategy: "fetch+convertFileSrc" };
    }
    lastErr = new Error(`fetch !ok status=${resp.status}`);
  } catch (e) {
    lastErr = e;
    dbg(`${dbgPrefix} [A] ERROR`, e?.message || String(e));
  }

  // Estrategia B: Filesystem.readFile con Directory.Data + path relativo
  // send-intent escribe en getFilesDir() == Directory.Data en Capacitor.
  // Extraemos el nombre de fichero de la URL.
  try {
    const pathParts = absUrl.replace(/^file:\/\//, "").split("/");
    const filename = pathParts[pathParts.length - 1];
    dbg(`${dbgPrefix} [B] Filesystem.readFile {path:${filename}, directory:Data}`);
    const res = await Filesystem.readFile({
      path: filename,
      directory: Directory.Data,
    });
    const bytes = base64ToBytes(res.data);
    dbg(`${dbgPrefix} [B] OK size=${bytes.length}`);
    return { blob: new Blob([bytes]), strategy: "Filesystem.Data" };
  } catch (e) {
    lastErr = e;
    dbg(`${dbgPrefix} [B] ERROR`, e?.message || String(e));
  }

  // Estrategia C: Filesystem.readFile con path absoluto (algunos Capacitor 7
  // builds lo aceptan)
  try {
    dbg(`${dbgPrefix} [C] Filesystem.readFile {path:${absUrl}}`);
    const res = await Filesystem.readFile({ path: absUrl });
    const bytes = base64ToBytes(res.data);
    dbg(`${dbgPrefix} [C] OK size=${bytes.length}`);
    return { blob: new Blob([bytes]), strategy: "Filesystem.abs" };
  } catch (e) {
    lastErr = e;
    dbg(`${dbgPrefix} [C] ERROR`, e?.message || String(e));
  }

  throw lastErr || new Error("Las 3 estrategias de lectura fallaron");
}

async function descriptorToFile(item, idx) {
  const fallbackName = `archivo-${Date.now()}-${idx}`;
  const rawUrl = item?.url || "";
  let decoded = rawUrl;
  try {
    decoded = decodeURIComponent(rawUrl);
  } catch (_) {
    decoded = rawUrl;
  }
  const guessedName =
    item?.title || (decoded ? decoded.split("/").pop() : fallbackName);
  const mime = item?.type || "application/octet-stream";

  dbg(`item[${idx}]`, {
    title: item?.title,
    type: mime,
    rawUrl: rawUrl.slice(0, 120),
    decoded: decoded.slice(0, 120),
    guessedName,
  });

  if (!decoded) {
    dbg(`item[${idx}] sin url → descartado`);
    return null;
  }

  if (!decoded.startsWith("file://") && !decoded.startsWith("content://")) {
    dbg(`item[${idx}] no es fichero (es ${decoded.slice(0, 40)}…) → descartado`);
    return null;
  }

  const { blob, strategy } = await readFileBytes(decoded, `item[${idx}]`);
  const file = new File([blob], guessedName, { type: blob.type || mime });
  dbg(
    `item[${idx}] ✓ File creado vía '${strategy}' name=${file.name} size=${file.size} type=${file.type}`
  );
  return file;
}

function flatten(payload) {
  if (!payload) return [];
  const items = [];
  if (payload.url || payload.title) items.push(payload);
  if (Array.isArray(payload.additionalItems)) {
    for (const it of payload.additionalItems) items.push(it);
  }
  return items;
}

let processing = false;
let lastProcessedAt = 0;
let lastProcessedSignature = null;
const subscribers = new Set();

// Subscriber para que la UI pueda enterarse cuando lleguen archivos compartidos
// aunque la pantalla /send se montase ANTES de que terminara la conversión.
export function onSharedFiles(callback) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

function notifySubscribers(files) {
  for (const cb of subscribers) {
    try {
      cb(files);
    } catch (e) {
      dbg("subscriber error:", e?.message || String(e));
    }
  }
}

// Firma del intent para deduplicar entre eventos consecutivos. Android no
// limpia el intent entre llamadas a checkSendIntentReceived(), así que sin
// esto cualquier listener extra (appUrlOpen + sendIntentReceived + resume)
// re-procesaría los mismos archivos → bug "se duplican".
function signatureOfItems(items) {
  return items
    .map((it) => `${it?.url || ""}|${it?.title || ""}|${it?.type || ""}`)
    .join("\n");
}

// Llamado por Send.jsx tras un envío exitoso o cuando el usuario navega
// fuera. Limpia stash para que un evento tardío no reinyecte los mismos
// archivos en futuros envíos.
//
// IMPORTANTE: NO reseteamos `lastProcessedSignature`. Si lo hiciéramos, un
// evento tardío (resume/appUrlOpen) podría re-procesar el mismo intent que
// ya enviamos y reabrirnos el formulario con los mismos archivos.
export function clearSharedFiles() {
  if (typeof window !== "undefined") {
    window.__kapeiSharedFiles = null;
    window.__kapeiSharedAt = 0;
    window.__kapeiShareErrors = [];
  }
  dbg("clearSharedFiles() → stash limpiado (firma del intent conservada)");
}

async function handleIntent(navigate, source, { force = false } = {}) {
  if (processing) {
    dbg(`(${source}) ya procesando, ignoro`);
    return false;
  }
  if (!force && Date.now() - lastProcessedAt < 1500) {
    dbg(`(${source}) debounce reciente, ignoro`);
    return false;
  }
  processing = true;
  try {
    dbg(`(${source}) checkSendIntentReceived…`);
    let payload;
    try {
      payload = await SendIntent.checkSendIntentReceived();
    } catch (err) {
      dbg(`(${source}) sin intent pendiente:`, err?.message || err);
      return false;
    }
    dbg(`(${source}) payload bruto recibido. keys=${Object.keys(payload || {}).join(",")}`);

    const items = flatten(payload);
    dbg(`(${source}) items aplanados: ${items.length}`);
    if (items.length === 0) return false;

    // 🛡️ Deduplicación a nivel intent: si la firma coincide con el último
    // intent procesado con éxito, NO re-procesamos (evita el bug de
    // duplicación cuando appUrlOpen + sendIntentReceived disparan a la vez).
    const sig = signatureOfItems(items);
    if (sig === lastProcessedSignature) {
      dbg(`(${source}) ⏩ mismo intent ya procesado (firma idéntica), ignoro`);
      return false;
    }

    const files = [];
    const errors = [];
    for (let i = 0; i < items.length; i++) {
      try {
        const f = await descriptorToFile(items[i], i);
        if (f) files.push(f);
      } catch (e) {
        const msg = e?.message || String(e);
        errors.push(`item[${i}]: ${msg}`);
        dbg(`item[${i}] FALLÓ:`, msg);
      }
    }
    dbg(
      `(${source}) ✓ conversión completada: ${files.length}/${items.length} Files materializados, errores=${errors.length}`
    );

    if (files.length === 0) {
      // No tragamos: dejamos el log + un stash de errores para que la UI los muestre.
      if (typeof window !== "undefined") {
        window.__kapeiShareErrors = errors;
      }
      return false;
    }

    // Stash global ANTES de navegar — red de seguridad si el state se pierde.
    if (typeof window !== "undefined") {
      window.__kapeiSharedFiles = files;
      window.__kapeiSharedAt = Date.now();
      window.__kapeiShareErrors = errors;
      dbg(`(${source}) ✓ stash global guardado (${files.length} archivos)`);
    }

    lastProcessedAt = Date.now();
    lastProcessedSignature = sig;  // marca este intent como ya consumido

    // ⚠️ NO llamamos a SendIntent.finish().
    // El plugin implementa finish() como bridge.getActivity().finish(), lo
    // que en SendIntentActivity (descartada) cerraba solo la activity
    // temporal de compartir. Pero ahora los intent-filters están en
    // MainActivity (launchMode=singleTask), así que llamar a finish() cierra
    // toda la app justo cuando estamos a punto de mostrarle los archivos al
    // usuario. Logcat confirmó este bug: "pluginId: SendIntent, methodName:
    // finish → App paused → App stopped → App destroyed".
    //
    // No hay nada que limpiar: el intent ya se consumió leyendo
    // bridge.getActivity().getIntent() en checkSendIntentReceived; MainActivity
    // sigue viva y el WebView mantiene el state.
    dbg(`(${source}) ⏩ skip SendIntent.finish() (cerraría MainActivity)`);

    dbg(`(${source}) → navigate('/send?mode=quick&fromShare=1') con ${files.length} archivos`);
    navigate("/send?mode=quick&fromShare=1", {
      state: { sharedFiles: files },
    });
    dbg(`(${source}) ✓ navegación disparada`);
    // Por si /send ya estaba montado (warm start sobre la misma pantalla)
    notifySubscribers(files);
    dbg(`(${source}) ✓ subscribers notificados (${subscribers.size})`);
    return true;
  } finally {
    processing = false;
  }
}

async function coldStartRetry(navigate) {
  const delays = [0, 250, 750, 1500, 3000, 5000];
  for (const d of delays) {
    if (d > 0) await new Promise((r) => setTimeout(r, d));
    const ok = await handleIntent(navigate, `cold+${d}ms`);
    if (ok) return;
  }
  dbg("cold start: ningún intent procesable tras todos los reintentos");
}

export function initShareReceiver(navigate) {
  if (!isNative()) {
    dbg("plataforma web — listener nativo desactivado");
    return;
  }
  dbg("plataforma nativa detectada, instalando listeners");

  coldStartRetry(navigate);

  CapacitorApp.addListener("appUrlOpen", (data) => {
    dbg("appUrlOpen:", data?.url);
    handleIntent(navigate, "appUrlOpen", { force: true });
  });
  CapacitorApp.addListener("resume", () => {
    dbg("App resume");
    handleIntent(navigate, "resume", { force: true });
  });

  if (typeof window !== "undefined") {
    window.addEventListener("sendIntentReceived", () => {
      dbg("window event: sendIntentReceived");
      handleIntent(navigate, "sendIntentReceived", { force: true });
    });
  }
}

export function getShareDebugLog() {
  return DEBUG.slice();
}

// Permite que Send.jsx escriba en el mismo log para correlacionar eventos.
export function shareDbg(...args) {
  dbg("[Send.jsx]", ...args);
}

export function getShareErrors() {
  if (typeof window === "undefined") return [];
  return window.__kapeiShareErrors || [];
}

export function consumeStashedSharedFiles() {
  if (typeof window === "undefined") return null;
  const files = window.__kapeiSharedFiles;
  const at = window.__kapeiSharedAt || 0;
  if (!files || Date.now() - at > 60000) return null;
  window.__kapeiSharedFiles = null;
  window.__kapeiSharedAt = 0;
  return files;
}
